// Banner-slogan editor stack — shared between the agency and affiliate
// settings pages (and any future proptree-settings surface). All the
// pieces it needs are bundled here so the consumer just renders
// `<BannerSlogansEditor value={...} onChange={...} />` and gets the
// preset toggles, custom-slogan list, add modal, and per-row chrome.
//
// The agency page still has its own inline copy of this stack for now;
// it can migrate to the shared one in a follow-up. Until then both
// versions must stay behavior-compatible.
import * as React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import type { BannerSlogans, ProptreeEnumValue } from '@proptr/shared';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Label } from './ui/label.js';
import { cn } from '../lib/utils.js';

// Hard limits — mirrored on the public renderer so the API can enforce
// identical bounds when the save path lands.
export const SLOGAN_CHAR_LIMIT = 60;
export const MAX_CUSTOM_SLOGANS = 10;

const PRESET_LABELS: Record<keyof BannerSlogans['presets'], string> = {
  activeListings: 'Active listings count',
  agents: 'Number of agents',
  listingsForSale: 'Listings for sale',
  listingsForRent: 'Listings for rent',
};
const PRESET_PLACEHOLDERS: Record<keyof BannerSlogans['presets'], string> = {
  activeListings: '“N active listings”',
  agents: '“N agents”',
  listingsForSale: '“N listings for sale”',
  listingsForRent: '“N listings for rent”',
};

// Default `BannerSlogans` value — handy for `initialFormState` calls or
// when a doc lacks the field entirely.
export function defaultBannerSlogans(): BannerSlogans {
  return {
    enabled: false,
    presets: {
      activeListings: false,
      agents: false,
      listingsForSale: false,
      listingsForRent: false,
    },
    customs: [],
  };
}

// Strip empty / whitespace-only customs before they go over the wire
// (preview patch or save). Avoids the marquee rotating blank entries
// while the user is mid-typing a new line.
export function cleanBannerSlogans(value: BannerSlogans): BannerSlogans {
  return {
    enabled: value.enabled,
    presets: { ...value.presets },
    customs: value.customs.map((s) => s.trim()).filter((s) => s.length > 0),
  };
}

// Preset + custom-slogan editor. Some presets disable themselves when
// their underlying public-page surface isn't visible (e.g. listings
// hidden → no "active listings" preset). The disable reason surfaces as
// a contextual hint so the user understands why a row is greyed out.
export function BannerSlogansEditor({
  value,
  onChange,
  propertiesType,
  mainFeed,
  showAgentFeed,
}: {
  value: BannerSlogans;
  onChange: (next: BannerSlogans) => void;
  propertiesType: ProptreeEnumValue | null;
  mainFeed: ProptreeEnumValue | null;
  // Affiliates don't expose an "agents feed" toggle (no agents grid on
  // an individual affiliate page), so callers can pass `null` and the
  // editor just always treats the agents preset as unavailable.
  showAgentFeed: ProptreeEnumValue | null;
}) {
  const presetKeys = Object.keys(value.presets) as Array<
    keyof BannerSlogans['presets']
  >;
  const typeValue = propertiesType?.value ?? null;
  const listingsHidden = mainFeed?.value === 'disabled';
  const agentsHidden =
    showAgentFeed === null ? true : showAgentFeed.value === 'disabled';

  const reasonFor = (
    key: keyof BannerSlogans['presets'],
  ): string | null => {
    if (key === 'activeListings' && listingsHidden) {
      return 'n/a — Show listings is disabled';
    }
    if (key === 'agents' && agentsHidden) {
      return 'n/a — Agents grid is not shown on this proptree';
    }
    if (key === 'listingsForSale') {
      if (listingsHidden) return 'n/a — Show listings is disabled';
      if (typeValue === 'rent') return 'n/a — Type is set to Rentals only';
    }
    if (key === 'listingsForRent') {
      if (listingsHidden) return 'n/a — Show listings is disabled';
      if (typeValue === 'sale') return 'n/a — Type is set to Sale only';
    }
    return null;
  };

  const presetUnavailable: Record<keyof BannerSlogans['presets'], boolean> = {
    activeListings: reasonFor('activeListings') !== null,
    agents: reasonFor('agents') !== null,
    listingsForSale: reasonFor('listingsForSale') !== null,
    listingsForRent: reasonFor('listingsForRent') !== null,
  };

  const setPreset = (key: keyof BannerSlogans['presets'], v: boolean) => {
    if (presetUnavailable[key]) return;
    onChange({
      ...value,
      presets: { ...value.presets, [key]: v },
    });
  };

  const [addOpen, setAddOpen] = React.useState(false);
  const addCustom = (text: string) => {
    if (value.customs.length >= MAX_CUSTOM_SLOGANS) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    onChange({ ...value, customs: [...value.customs, trimmed] });
  };
  const updateCustom = (i: number, text: string) => {
    const clipped = text.slice(0, SLOGAN_CHAR_LIMIT);
    onChange({
      ...value,
      customs: value.customs.map((s, j) => (j === i ? clipped : s)),
    });
  };
  const removeCustom = (i: number) =>
    onChange({
      ...value,
      customs: value.customs.filter((_, j) => j !== i),
    });

  const customCount = value.customs.length;
  const remainingCustomSlots = MAX_CUSTOM_SLOGANS - customCount;

  return (
    <div className="mt-3 space-y-4">
      {/* Predefined slogans */}
      <div className="rounded-md border border-border bg-card">
        <div className="border-b border-[hsl(var(--hairline))] px-4 py-2">
          <div className="mono-label text-muted-foreground">
            Predefined slogans
          </div>
        </div>
        <div className="divide-y divide-[hsl(var(--hairline))]">
          {presetKeys.map((key) => {
            const reason = reasonFor(key);
            const unavailable = reason !== null;
            return (
              <div
                key={key}
                className={cn(
                  'flex items-center justify-between gap-3 px-4 py-2.5',
                  unavailable && 'opacity-50',
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="font-sans text-[13px] text-foreground">
                    {PRESET_LABELS[key]}
                  </div>
                  <div className="font-mono text-[10px] text-muted-foreground">
                    {PRESET_PLACEHOLDERS[key]}
                    <span aria-hidden className="px-1.5 opacity-60">
                      ·
                    </span>
                    {reason ?? 'only shows if more than one exist'}
                  </div>
                </div>
                <SwitchToggle
                  checked={!unavailable && value.presets[key]}
                  onChange={(v) => setPreset(key, v)}
                  ariaLabel={PRESET_LABELS[key]}
                  disabled={unavailable}
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Custom slogans */}
      <div className="rounded-md border border-border bg-card">
        <div className="flex items-center justify-between gap-3 border-b border-[hsl(var(--hairline))] px-4 py-2">
          <div className="mono-label text-muted-foreground">
            Custom slogans
          </div>
          <div className="font-mono text-[10px] text-muted-foreground">
            {customCount} / {MAX_CUSTOM_SLOGANS}
          </div>
        </div>
        {value.customs.length > 0 && (
          <div className="divide-y divide-[hsl(var(--hairline))]">
            {value.customs.map((text, i) => (
              <CustomSloganRow
                key={i}
                value={text}
                onChange={(v) => updateCustom(i, v)}
                onRemove={() => removeCustom(i)}
              />
            ))}
          </div>
        )}
        <div
          className={cn(
            'px-4 py-2',
            value.customs.length > 0 &&
              'border-t border-[hsl(var(--hairline))]',
          )}
        >
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setAddOpen(true)}
            disabled={remainingCustomSlots <= 0}
          >
            <Plus />
            Add slogan
          </Button>
        </div>
      </div>

      <AddSloganModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onAdd={addCustom}
      />
    </div>
  );
}

function AddSloganModal({
  open,
  onClose,
  onAdd,
}: {
  open: boolean;
  onClose: () => void;
  onAdd: (text: string) => void;
}) {
  const [text, setText] = React.useState('');
  const [shown, setShown] = React.useState(open);
  const [visible, setVisible] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (open) {
      setShown(true);
      setText('');
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      window.addEventListener('keydown', onKey);
      const r1 = requestAnimationFrame(() => {
        const r2 = requestAnimationFrame(() => {
          setVisible(true);
          inputRef.current?.focus();
        });
        return () => cancelAnimationFrame(r2);
      });
      return () => {
        cancelAnimationFrame(r1);
        document.body.style.overflow = prev;
        window.removeEventListener('keydown', onKey);
      };
    }
    setVisible(false);
    const t = setTimeout(() => setShown(false), 200);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!shown) return null;

  const trimmed = text.trim();
  const remaining = SLOGAN_CHAR_LIMIT - text.length;
  const submit = () => {
    if (!trimmed) return;
    onAdd(trimmed);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={onClose}
        className={cn(
          'absolute inset-0 bg-black/60 transition-opacity duration-200 ease-out',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        className={cn(
          'relative w-full max-w-md overflow-hidden rounded-2xl border border-[hsl(var(--hairline))] bg-card shadow-pop transition-all duration-200 ease-out',
          visible
            ? 'translate-y-0 scale-100 opacity-100'
            : 'translate-y-2 scale-95 opacity-0',
        )}
      >
        <div className="border-b border-[hsl(var(--hairline))] px-5 py-4">
          <h2 className="font-display text-[20px] font-medium leading-none tracking-[-0.01em]">
            Add slogan
          </h2>
          <p className="mt-1.5 font-sans text-[12px] text-muted-foreground">
            Short tagline to rotate in the marquee. Up to {SLOGAN_CHAR_LIMIT}{' '}
            characters.
          </p>
        </div>
        <div className="px-5 py-5">
          <Label htmlFor="add-slogan-input">Slogan</Label>
          <Input
            id="add-slogan-input"
            ref={inputRef}
            value={text}
            onChange={(e) =>
              setText(e.target.value.slice(0, SLOGAN_CHAR_LIMIT))
            }
            maxLength={SLOGAN_CHAR_LIMIT}
            placeholder="e.g. Find your dream home"
            className="mt-1 bg-muted/30"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
          />
          <div className="mt-1.5 text-right">
            <span
              className={cn(
                'font-mono text-[10px] tabular-nums',
                remaining <= 10 ? 'text-amber-600' : 'text-muted-foreground',
              )}
            >
              {text.length}/{SLOGAN_CHAR_LIMIT}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[hsl(var(--hairline))] bg-muted/30 px-5 py-3">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={submit} disabled={!trimmed}>
            Add slogan
          </Button>
        </div>
      </div>
    </div>
  );
}

function CustomSloganRow({
  value,
  onChange,
  onRemove,
}: {
  value: string;
  onChange: (v: string) => void;
  onRemove: () => void;
}) {
  const remaining = SLOGAN_CHAR_LIMIT - value.length;
  return (
    <div className="flex items-center gap-2 px-4 py-2">
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={SLOGAN_CHAR_LIMIT}
        placeholder="e.g. Find your dream home"
        className="h-9 flex-1 bg-muted/30"
      />
      <span
        className={cn(
          'shrink-0 font-mono text-[10px] tabular-nums',
          remaining <= 10 ? 'text-amber-600' : 'text-muted-foreground',
        )}
      >
        {value.length}/{SLOGAN_CHAR_LIMIT}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label="Remove slogan"
        className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
      >
        <Trash2 />
      </Button>
    </div>
  );
}

function SwitchToggle({
  checked,
  onChange,
  ariaLabel,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  ariaLabel: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={ariaLabel}
      onClick={() => {
        if (disabled) return;
        onChange(!checked);
      }}
      disabled={disabled}
      className={cn(
        'relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
        disabled && 'cursor-not-allowed',
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform',
          checked ? 'translate-x-[18px]' : 'translate-x-[2px]',
        )}
      />
    </button>
  );
}
